import { Type, type Static } from 'typebox';

export const ViewTypeSchema = Type.Union(
  [
    Type.Literal('normal'),
    Type.Literal('pivot'),
    Type.Literal('pivot_charts'),
  ],
  {
    description: '视图类型: normal(标准) / pivot(透视表) / pivot_charts(透视图)',
  },
);

export const OptionalViewTypeSchema = Type.Optional(
  Type.Union([ViewTypeSchema, Type.Null()], {
    description: '可选视图类型。省略或传 null 时表示全部支持类型',
  }),
);

export const UidSchema = Type.String({
  description: '视图 uid(优先)',
  minLength: 1,
});

export const NameSchema = Type.String({
  description: '视图名称',
  minLength: 1,
});

export const ViewDataSchema = Type.Any({
  description: '完整页面能力配置对象。字段名、枚举和值结构必须来自 page_view_config_data 返回的数据。',
});

export const ApplyModeSchema = Type.Union(
  [Type.Literal('create'), Type.Literal('temporary')],
  {
    description: 'create=保存为新视图, temporary=仅临时应用到当前页面',
    default: 'create',
  },
);

export const VisibilitySchema = Type.Union(
  [Type.Literal('personal'), Type.Literal('public')],
  {
    description: 'create 模式下可见性,默认 personal',
    default: 'personal',
  },
);

export const ViewCapabilityParamsSchema = Type.Object(
  {
    view_type: OptionalViewTypeSchema,
  },
  {
    description: '判断当前页面支持哪些视图类型。view_type 省略时返回全部支持情况。',
    additionalProperties: false,
  },
);

export const ViewConfigDataParamsSchema = Type.Object(
  {
    view_type: ViewTypeSchema,
  },
  {
    description: '获取构造指定类型视图配置所需的字段清单、搜索字段、现有视图和样例数据。',
    additionalProperties: false,
  },
);

export const ViewLookupParamsSchema = Type.Object(
  {
    view_type: ViewTypeSchema,
    uid: Type.Optional(UidSchema),
    name: Type.Optional(NameSchema),
  },
  {
    description: '按 uid 或名称定位已有视图。uid 优先。',
    additionalProperties: false,
  },
);

export const ViewConfigParamsSchema = Type.Object(
  {
    view_type: ViewTypeSchema,
    apply_mode: Type.Optional(ApplyModeSchema),
    name: Type.Optional(
      Type.String({
        description: '视图名称。apply_mode=create 时必填',
        minLength: 1,
      }),
    ),
    view_data: ViewDataSchema,
    is_system: Type.Optional(
      Type.Boolean({
        description: '是否设为系统视图',
      }),
    ),
    visibility: Type.Optional(VisibilitySchema),
    activate: Type.Optional(
      Type.Boolean({
        description: 'create 模式下创建后是否切换,默认 true',
      }),
    ),
  },
  {
    description: '配置视图。支持创建新视图或临时应用配置。',
    additionalProperties: false,
  },
);

export const ViewUpdateParamsSchema = Type.Object(
  {
    view_type: ViewTypeSchema,
    uid: Type.Optional(UidSchema),
    name: Type.Optional(NameSchema),
    view_data: ViewDataSchema,
    is_system: Type.Optional(
      Type.Boolean({
        description: '是否同步更新系统视图标记',
      }),
    ),
  },
  {
    description: '覆盖更新已有视图的完整配置。',
    additionalProperties: false,
  },
);

export const ViewRenameParamsSchema = Type.Object(
  {
    view_type: ViewTypeSchema,
    uid: Type.Optional(UidSchema),
    name: Type.Optional(NameSchema),
    new_name: Type.String({
      description: '新的视图名称',
      minLength: 1,
    }),
  },
  {
    description: '重命名已有视图。',
    additionalProperties: false,
  },
);

export type ViewCapabilityParams = Static<typeof ViewCapabilityParamsSchema>;
export type ViewConfigDataParams = Static<typeof ViewConfigDataParamsSchema>;
export type ViewLookupParams = Static<typeof ViewLookupParamsSchema>;
export type ViewConfigParams = Static<typeof ViewConfigParamsSchema>;
export type ViewUpdateParams = Static<typeof ViewUpdateParamsSchema>;
export type ViewRenameParams = Static<typeof ViewRenameParamsSchema>;
